import { createSlice } from '@reduxjs/toolkit';

// Per-user localStorage key for the selected bid (ticket #295). Kept apart
// from the selected project: selecting one never clears the other.
const STORAGE_KEY_PREFIX = 'fieldtime_selected_bid_';

export const getBidStorageKey = (userId) => `${STORAGE_KEY_PREFIX}${userId}`;

/**
 * Read the stored bid for a user. Returns the parsed
 * { id, bidNumber, name, status } object, or null if nothing is stored or
 * parsing fails.
 */
export const readStoredBid = (userId) => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(getBidStorageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeStoredBid = (userId, bid) => {
  if (!userId) return;
  try {
    localStorage.setItem(getBidStorageKey(userId), JSON.stringify(bid));
  } catch {
    // Ignore quota / serialization errors — Redux state remains the source of truth.
  }
};

export const clearStoredBid = (userId) => {
  if (!userId) return;
  try {
    localStorage.removeItem(getBidStorageKey(userId));
  } catch {
    // Ignore.
  }
};

/** The snapshot kept in Redux and localStorage, from a full API bid. */
export const toBidSnapshot = ({ id, bidNumber, name, status }) => ({
  id,
  bidNumber,
  name,
  status,
});

const initialBidState = {
  selectedBid: null, // { id, bidNumber, name, status } or null
  isPanelOpen: false,
};

const bidSlice = createSlice({
  name: 'bid',
  initialState: initialBidState,
  reducers: {
    selectBid(state, action) {
      state.selectedBid = toBidSnapshot(action.payload);
    },
    clearBid(state) {
      state.selectedBid = null;
    },
    openPanel(state) {
      state.isPanelOpen = true;
    },
    closePanel(state) {
      state.isPanelOpen = false;
    },
  },
});

export const bidActions = bidSlice.actions;

export default bidSlice.reducer;
